import { useEffect, useMemo, useState } from "react";
import { ImageType, MediaPageProps } from "../../../types/app/mediaTypes";
import { appConstants } from "../../../redux/action-constants/inapp.constants";
import { LoadingItem } from "../../../shared/components/loading";
import ReportDisplay from "./ReportDisplay";
import { AlertMsg } from "../../../shared/components/alert-msg";
import { MediaDetailView } from "./MediaDetailView";

export interface ActionProps {
    pageProps: MediaPageProps;
    payload?: string | FormData | "CLEAR" | { id: string; url: string; names: string[]; name: string | null; imageStatus: number; };
    isNewBatch?: boolean;
    otherNames?: string[];
    uploadId?: string;
}

const getImageGalleryAction = async ({ pageProps, payload, isNewBatch }: ActionProps) => {

    await pageProps.getImageGalleryAction(payload, isNewBatch);
}

const deleteImageAction = async ({ pageProps, payload }: ActionProps) => {
    await pageProps.deleteImageAction(payload)
}

const updateProductImageAction = async ({ payload, pageProps }: ActionProps) => {
    await pageProps.updateProductImageAction(payload as { id: string; url: string; names: string[]; name: string; imageStatus: number; } | "CLEAR")
}

const getProductCategoriesAction = async ({ pageProps, payload }: ActionProps) => {

    await pageProps.getProductCategoriesAction(payload as string);
}

const uploadProductImageAction = async ({
    pageProps,
    payload,
    otherNames,
    uploadId,
}: ActionProps) => {
    return await pageProps.uploadProductImageAction(
        payload,
        otherNames,
        uploadId,
    );
};
const InAppContent = ({ pageProps }: { pageProps: MediaPageProps }) => {
    const [selected, setSelected] = useState<number[]>([]);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(100)
    const [isNewBatch, setIsNewBatch] = useState<boolean>(false)
    const [isNewBatchLoading, setIsNewBatchLoading] = useState<boolean>(false)
    const [isSearchLoading, setSearchLoading] = useState<boolean>(false)
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [searchValue, setSearchValue] = useState<string>("");
    const [currentImageDetailView, setCurrentImageDetailView] = useState<ImageType | null>(null)
    const [currentIndex, setCurrentIndex] = useState<number | null>(null);
    const [openMediaDetailView, setOpenMediaDetailView] = useState<boolean>(false);


    const [sortOption, setSortOption] = useState("new-to-old");
    const [loadImgs, setLoadImgs] = useState<number>(1)

    const sortedGalleryData = useMemo(() => {
        const galleryData = pageProps?.getImageGalleryRequest?.galleryData || [];
        setLoadImgs((prev) => prev + 1);


        return [...galleryData].sort((a, b) => {
            if (!a || !b) return 0; // fallback if any item is null

            switch (sortOption) {
                case "new-to-old":
                    return new Date(b?.createdDate || 0).getTime() - new Date(a?.createdDate || 0).getTime();
                case "old-to-new":
                    return new Date(a?.createdDate || 0).getTime() - new Date(b?.createdDate || 0).getTime();
                case "a-to-z":
                    return (a?.name || "").localeCompare(b?.name || "");
                case "z-to-a":
                    return (b?.name || "").localeCompare(a?.name || "");
                case "large-to-small":
                    return (b?.size || 0) - (a?.size || 0);
                case "small-to-large":
                    return (a?.size || 0) - (b?.size || 0);
                default:
                    return 0;
            }
        });
    }, [pageProps?.getImageGalleryRequest?.galleryData, sortOption]);

    const handleNext = () => {
        if (currentIndex !== sortedGalleryData?.length) {
            setCurrentImageDetailView(sortedGalleryData[currentIndex as number + 1]);
            setOpenMediaDetailView(true);
            setCurrentIndex((prev) => prev as number + 1)
        }
    }

    const handlePrev = () => {
        if (currentIndex !== 0) {
            setCurrentImageDetailView(sortedGalleryData[currentIndex as number - 1]);
            setOpenMediaDetailView(true);
            setCurrentIndex((prev) => prev as number - 1)
        }
    }



    const handleDeleteImages = async (
        imageFilenames: string[],
    ): Promise<{
        isSuccess: boolean;
        isError: boolean;
    }> => {
        try {
            const results = await Promise.allSettled(
                imageFilenames.map((filename) =>
                    deleteImageAction({
                        pageProps,
                        payload: `fileName=${filename}`,
                    })
                )
            );

            const hasError = results.some((res) => res.status === "rejected");

            if (!hasError) {
                getImageGalleryAction({ pageProps, payload: `page=${pageNumber}&size=${pageSize}&fileName=${searchTerm}`, isNewBatch }).then(() => {
                    setIsNewBatchLoading(false)
                    setSearchLoading(false)
                })
            }

            return {
                isSuccess: !hasError,
                isError: hasError,
            };
        } catch (err) {
            return {
                isSuccess: false,
                isError: true,
            };
        }
    };

    type ImageData = {
        name: string;
        categoryName: string;
        file: File;
        otherNames: string[];
    };

    const handleUploadImages = async (
        images: ImageData[],
        onProgress?: (progress: number) => void
    ): Promise<{
        isSuccess: boolean;
        isError: boolean;
    }> => {
        try {
            const progressArr = new Array(images.length).fill(0);
            const fakeIntervals: NodeJS.Timeout[] = [];

            const results = await Promise.allSettled(
                images.map((imageData, idx) => {
                    const formData = new FormData();
                    formData.append("ProductName", imageData.name);
                    formData.append("CategoryName", imageData.categoryName);
                    formData.append("ImageFile", imageData.file);
                    imageData.otherNames.forEach((name: string) => {
                        formData.append("OtherNames", name);
                    });

                    fakeIntervals[idx] = setInterval(() => {
                        if (progressArr[idx] < 95) {
                            progressArr[idx] += 1;
                            const overall = Math.round(
                                progressArr.reduce((sum, p) => sum + p, 0) / images.length
                            );
                            if (onProgress) onProgress(overall);
                        }
                    }, 50);

                    return uploadProductImageAction({
                        pageProps,
                        payload: formData,
                        otherNames: imageData.otherNames,
                        uploadId: `upload-${idx}`,
                    }).then((res) => {
                        clearInterval(fakeIntervals[idx]);
                        progressArr[idx] = 100;
                        const overall = Math.round(
                            progressArr.reduce((sum, p) => sum + p, 0) / images.length
                        );
                        if (onProgress) onProgress(overall);
                        return res;
                    }).catch((err) => {
                        clearInterval(fakeIntervals[idx]);
                        throw err;
                    });
                })
            );

            const hasError = results.some((res) => res.status === "rejected");

            if (
                pageProps?.uploadProductImageRequest?.request_status ===
                "UPLOAD_PRODUCT_PICTURE_SUCCESS"
            ) {
                getImageGalleryAction({
                    pageProps,
                    payload: `page=${pageNumber}&size=${pageSize}&fileName=${searchTerm}`,
                    isNewBatch,
                }).then(() => {
                    setIsNewBatchLoading(false);
                    setSearchLoading(false);
                });
            }

            return {
                isSuccess: !hasError,
                isError: hasError,
            };
        } catch (err) {
            return {
                isSuccess: false,
                isError: true,
            };
        }
    };

    const handleUpdateImage = async ({
        updatedImageData
    }: {
        updatedImageData: {
            id: string;
            url: string;
            names: string[];
            name: string | null;
            imageStatus: number;
        };
    }): Promise<{
        isSuccess: boolean;
        isError: boolean;
    }> => {
        try {
            const response: any = await updateProductImageAction({
                pageProps,
                payload: updatedImageData,
            });

            if (
                pageProps?.updateProductImageRequest?.request_status === "UPDATE_PRODUCT_PICTURE_SUCCESS"
            ) {
                getImageGalleryAction({
                    pageProps,
                    payload: `page=${pageNumber}&size=${pageSize}&fileName=${searchTerm}`,
                    isNewBatch,
                }).then(() => {
                    setIsNewBatchLoading(false);
                    setSearchLoading(false);
                });
            }
            if (response?.status && response.status >= 200 && response.status < 300) {
                return { isSuccess: true, isError: false };
            }


            return { isSuccess: true, isError: false };
        } catch (error: any) {
            return { isSuccess: false, isError: true };
        }
    };

    const toggleSelect = (index: number) => {
        setSelected((prev) =>
            prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
        );
    };

    const handleEdit = (index: number) => {
        setCurrentIndex(index);
        setCurrentImageDetailView(sortedGalleryData[index]);
        setOpenMediaDetailView(true);
    };

    useEffect(() => {
        getImageGalleryAction({ pageProps, payload: `page=${pageNumber}&size=${pageSize}&fileName=${searchTerm}`, isNewBatch }).then(() => {
            setIsNewBatchLoading(false)
            setSearchLoading(false)
        })
    }, [pageNumber, pageSize, isNewBatch, searchTerm]);


    useEffect(() => {
        uploadProductImageAction({
            pageProps,
            payload: "CLEAR"
        })
        deleteImageAction({
            pageProps,
            payload: "CLEAR"
        })
    }, [])

    const handleResetUploadProductImage = () => {
        uploadProductImageAction({
            pageProps,
            payload: "CLEAR"
        })
    }
    const handleSearch = (value: string) => {
        setIsNewBatch(false)
        setSearchTerm(value)
        setSearchLoading(true)
        setPageNumber(1)
    }

    const handleClearSelection = () => {
        setSelected([]);
    };


    const handleSelectAll = () => {
        if (selected.length === sortedGalleryData?.length) return;

        const allIndexes = Array.from({ length: sortedGalleryData?.length }, (_, i) => i);
        setSelected(allIndexes);
    };


    const handleLoadMore = () => {
        setIsNewBatchLoading(true)
        setIsNewBatch(true);
        setPageNumber(prev => prev + 1); // ✅ always fresh
    };

    return (
        <div className="inapp-content">
            <div className="page-content dashboard">
                {(pageProps?.getImageGalleryRequest?.request_status === appConstants.GET_IMAGE_GALLERY_PENDING && !isSearchLoading) || (pageProps?.deleteImageRequest?.request_status === appConstants.DELETE_IMAGE_PENDING) &&
                    <LoadingItem />
                }
                {pageProps?.deleteImageRequest?.request_status === appConstants.DELETE_IMAGE_FAILURE &&
                    <AlertMsg type="error" message={pageProps.deleteImageRequest?.request_data.error} />
                }
                {pageProps?.updateProductImageRequest?.request_status === appConstants.UPDATE_PRODUCT_PICTURE_FAILURE &&
                    <AlertMsg type="error" message={pageProps.updateProductImageRequest?.request_data.error} />
                }
                {
                    (
                        pageProps?.getImageGalleryRequest?.request_status === appConstants.GET_IMAGE_GALLERY_SUCCESS ||
                        pageProps?.getImageGalleryRequest?.request_status === appConstants.GET_IMAGE_GALLERY_NEWBATCH || isSearchLoading
                    ) &&
                    <ReportDisplay
                        selected={selected}
                        handleEdit={handleEdit}
                        toggleSelect={toggleSelect}
                        pageProps={pageProps}
                        onLoadMore={handleLoadMore}
                        isNewBatchLoading={isNewBatchLoading}
                        onSearch={handleSearch}
                        isSearchLoading={isSearchLoading}
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                        onClearSelection={handleClearSelection}
                        onSelectAll={handleSelectAll}
                        setLoadimgs={setLoadImgs}
                        sortedGalleryData={sortedGalleryData}
                        loadImgs={loadImgs}
                        setSortOption={setSortOption}
                        sortOption={sortOption}
                        handleDeleteImages={handleDeleteImages}
                        handleUploadImages={handleUploadImages}
                        handleResetUploadProductImage={handleResetUploadProductImage}
                        getProductCategoriesAction={getProductCategoriesAction}
                    />
                }
                {pageProps.getImageGalleryRequest.request_status === appConstants.GET_IMAGE_GALLERY_FAILURE &&
                    <AlertMsg type="error" message={pageProps.getImageGalleryRequest?.request_data.error} />
                }

            </div>
            {openMediaDetailView &&
                <MediaDetailView show={openMediaDetailView}
                    handleDeleteImages={handleDeleteImages}
                    onClose={() => setOpenMediaDetailView(false)} isNext={
                        Array.isArray(pageProps?.getImageGalleryRequest?.galleryData) &&
                        currentIndex === pageProps.getImageGalleryRequest.galleryData.length - 1
                    }
                    isPrev={currentIndex === 0}
                    handleUpdateImage={handleUpdateImage}
                    onNext={handleNext} onPrev={handlePrev} imageData={currentImageDetailView} />
            }
        </div>
    );
};

export default InAppContent